// frontend/packages/business-console/src/services/inboxService.js
//
// Thin wrapper over /api/v2/inbox/*. Business context is supplied via the
// x-business-id header (axios interceptor in @upryzze/services), so callers
// don't pass businessId.

import { api } from '@upryzze/services';

/**
 * GET /api/v2/inbox/conversations
 *
 * `source`:
 *   - omitted        → backend default: excludes freeAudit (primary inbox)
 *   - "contactForm"  → only contact-form leads
 *   - "freeAudit"    → only free-audit leads
 *   - "smsInbound"   → only Twilio-inbound SMS conversations
 *   - "all"          → everything including freeAudit (explicit escape hatch)
 *
 * @param {{
 *   status?: string,
 *   source?: 'contactForm'|'freeAudit'|'smsInbound'|'all',
 *   q?: string,
 *   before?: string|Date,
 *   limit?: number,
 *   startDate?: string,
 *   endDate?: string
 * }} params
 */
export async function listConversations(params = {}) {
  const { data } = await api.get('/v2/inbox/conversations', { params });
  return data || { items: [], hasMore: false, nextCursor: null };
}

/**
 * GET /api/v2/inbox/conversations/summary
 *
 * Used for the nav badge. `unread` + `open` mirror the default listing
 * (exclude freeAudit). `freeAuditUnread` / `freeAuditOpen` are parallel
 * counters for a dedicated "Free audits" badge.
 */
export async function getInboxSummary() {
  const { data } = await api.get('/v2/inbox/conversations/summary');
  return (
    data || {
      unread: 0,
      open: 0,
      freeAuditUnread: 0,
      freeAuditOpen: 0,
      quarantined: 0,
    }
  );
}

/**
 * GET /api/v2/inbox/conversations/:id
 */
export async function getConversation(id) {
  if (!id) throw new Error('Conversation id is required');
  const { data } = await api.get(
    `/v2/inbox/conversations/${encodeURIComponent(id)}`
  );
  return data;
}

/**
 * PATCH /api/v2/inbox/conversations/:id
 * Whitelist: { status?: "open"|"closed"|"archived", markRead?: boolean }
 */
export async function updateConversation(id, updates = {}) {
  if (!id) throw new Error('Conversation id is required');
  const { data } = await api.patch(
    `/v2/inbox/conversations/${encodeURIComponent(id)}`,
    updates
  );
  return data;
}

/**
 * POST /api/v2/inbox/conversations/:id/messages
 * Body: { bodyText, subject? }
 */
export async function sendReply(id, { bodyText, subject } = {}) {
  if (!id) throw new Error('Conversation id is required');
  if (!bodyText || !String(bodyText).trim()) {
    throw new Error('Reply body is required');
  }
  const body = { bodyText: String(bodyText) };
  if (subject) body.subject = String(subject);
  const { data } = await api.post(
    `/v2/inbox/conversations/${encodeURIComponent(id)}/messages`,
    body
  );
  return data;
}

/**
 * POST /api/v2/inbox/conversations/:id/promote-to-crm
 * Sets the linked Customer.inCrm flag.
 */
export async function promoteToCrm(id) {
  if (!id) throw new Error('Conversation id is required');
  const { data } = await api.post(
    `/v2/inbox/conversations/${encodeURIComponent(id)}/promote-to-crm`
  );
  return data;
}

/**
 * DELETE /api/v2/inbox/conversations/:id
 * Soft-delete — the row is hidden from every inbox query going forward.
 * Frontend gates this behind a typed-DELETE modal on the Archived tab.
 */
export async function deleteConversation(id) {
  if (!id) throw new Error('Conversation id is required');
  const { data } = await api.delete(
    `/v2/inbox/conversations/${encodeURIComponent(id)}`
  );
  return data;
}

/**
 * POST /api/v2/inbox/conversations/:id/draft-reply
 * Returns { draft: string|null }. Never sends; the caller pre-fills the
 * composer with the draft. `draft` is null when nothing usable was produced.
 */
export async function draftReply(id) {
  if (!id) throw new Error('Conversation id is required');
  const { data } = await api.post(
    `/v2/inbox/conversations/${encodeURIComponent(id)}/draft-reply`
  );
  return data || { draft: null };
}

/**
 * GET /api/v2/inbox/reply-policy -> the tenant's reply voice + do/don't rules.
 */
export async function getReplyPolicy() {
  const { data } = await api.get('/v2/inbox/reply-policy');
  return data || {};
}

/**
 * PUT /api/v2/inbox/reply-policy (businessadmin) -> saves + returns the policy.
 */
export async function updateReplyPolicy(patch = {}) {
  const { data } = await api.put('/v2/inbox/reply-policy', patch);
  return data || {};
}
